import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot } from './firestore-compat';
import { hasPermission, CAPABILITIES } from './config/permissions';
import './Home.css';

const SUPER_ADMIN = 'alexandrebareaoliveira@gmail.com';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function fmtDate() {
  return new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Atalho (compacto: ícone + nome) ───────────────────────────────────────────
function Atalho({ item, cor }) {
  const conteudo = (
    <>
      <span className="hm-atalho-ic">{item.icon}</span>
      <span className="hm-atalho-nome">{item.title}</span>
    </>
  );
  const estilo = { '--hm-cor': cor };
  if (item.href) return <a className="hm-atalho" style={estilo} href={item.href} target="_blank" rel="noopener noreferrer">{conteudo}</a>;
  return <Link className="hm-atalho" style={estilo} to={item.to}>{conteudo}</Link>;
}

// ── Bloco (um grupo do menu) ──────────────────────────────────────────────────
function Bloco({ icon, titulo, cor, itens }) {
  const visiveis = itens.filter(Boolean);
  if (!visiveis.length) return null;
  return (
    <div className="hm-bloco" style={{ '--hm-cor': cor }}>
      <div className="hm-bloco-cab">
        <span className="hm-bloco-ic">{icon}</span>
        <strong>{titulo}</strong>
        <span className="hm-bloco-qtd">{visiveis.length}</span>
      </div>
      <div className="hm-atalhos">
        {visiveis.map(it => <Atalho key={it.to || it.href} item={it} cor={cor} />)}
      </div>
    </div>
  );
}

// ── Seção (mesmos títulos do menu lateral) ────────────────────────────────────
function Secao({ titulo, children }) {
  const blocos = React.Children.toArray(children).filter(Boolean);
  if (!blocos.length) return null;
  return (
    <section className="hm-secao">
      <div className="hm-secao-titulo">{titulo}</div>
      <div className={`hm-blocos ${blocos.length === 1 ? 'hm-blocos-unico' : ''}`}>{blocos}</div>
    </section>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState({});
  const [nome, setNome] = useState('');
  const [loading, setLoading] = useState(true);
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }
    const q = query(
      collection(db, getCollectionName('users')),
      where('email', '==', currentUser.email?.toLowerCase())
    );
    const unsub = onSnapshot(q, snap => {
      if (!snap.empty) {
        const d = snap.docs[0].data();
        setUserRoles(Array.isArray(d.roles) ? d.roles : d.role ? [d.role] : ['leitor']);
        setUserPermissions(d.permissions || {});
        setNome(d.agendaName || d.name || '');
      } else {
        setUserRoles(['leitor']);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [currentUser]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>;

  const isSuperAdmin = currentUser?.email === SUPER_ADMIN;
  const isAdmin = isSuperAdmin || userRoles.includes('admin') || userRoles.includes('administrador');
  const pode = cap => hasPermission(userRoles, cap, userPermissions);
  const modulo = chave => isAdmin || userPermissions?.[chave]?.view === true;
  // item visível: por capability (C), por módulo (M) ou para todos (F)
  const C = (cap, to, icon, title) => (pode(cap) ? { to, icon, title } : null);
  const M = (chave, to, icon, title) => (modulo(chave) ? { to, icon, title } : null);
  const F = (to, icon, title) => ({ to, icon, title });

  const primeiroNome = (nome || currentUser?.email?.split('@')[0] || '').split(/\s+/)[0];
  const nomeBonito = primeiroNome.charAt(0).toUpperCase() + primeiroNome.slice(1);

  return (
    <div className="hm-pagina">
      {/* ── Saudação ─────────────────────────────────────────────────────────── */}
      <div className="hm-saudacao">
        <div>
          <div className="hm-data">{fmtDate()}</div>
          <h1>👋 {greeting()}, {nomeBonito}!</h1>
          <div className="hm-sub">Bem-vindo ao Quality — escolha por onde começar</div>
        </div>
        <div className="hm-saudacao-botoes">
          <Link to="/gestao-captacao?fullscreen=1">Painel</Link>
          <Link to="/dashboard-hub">Dashboard</Link>
        </div>
      </div>

      <div className="hm-corpo">
        {/* ── Visão geral: o dia a dia (agenda, e-mail, convites, WhatsApp) ── */}
        <Secao titulo="Visão geral">
          <Bloco icon="🗓️" titulo="Meu dia" cor="#00897b" itens={[
            F('/minha-agenda', '📅', 'Minha Agenda'),
            M('email', '/email', '✉️', 'E-mail'),
            C(CAPABILITIES.VIEW_CONVITES, '/convites', '📨', 'Convites de Agenda'),
            M('teste_jarvis', '/zaping', '💬', 'Zaping'),
          ]} />
        </Secao>

        {/* ── Gestão ─────────────────────────────────────────────────────────── */}
        <Secao titulo="Gestão">
          <Bloco icon="📋" titulo="Captação" cor="#6366f1" itens={[
            C(CAPABILITIES.VIEW_GESTAO_OCORRENCIAS, '/gestao-captacao', '📋', 'Gestão Captação'),
            C(CAPABILITIES.VIEW_AGENDA_HOMECARE, '/agenda-captacao', '📅', 'Agenda Captação'),
            C(CAPABILITIES.VIEW_OCORRENCIAS_SETORES, '/ocorrencias-setores', '🏥', 'Minhas Ocorrências'),
          ]} />
          <Bloco icon="👥" titulo="Operacional" cor="#a855f7" itens={[
            C(CAPABILITIES.VIEW_GESTAO_PACIENTES, '/gestao-pacientes', '👥', 'Gestão Pacientes'),
            C(CAPABILITIES.VIEW_AGENDA_HOMECARE, '/agenda-homecare', '🗓', 'Agenda Homecare'),
            C(CAPABILITIES.VIEW_GESTAO_ESCALAS, '/escalas', '📐', 'Escalas Técnicos'),
            C(CAPABILITIES.VIEW_FORMULARIO_PASSAGEM, '/formulario-passagem', '📄', 'Form. Passagem'),
            C(CAPABILITIES.VIEW_GESTAO_PASSAGENS, '/gestao-passagens', '📊', 'Gestão Passagens'),
          ]} />
          <Bloco icon="🩺" titulo="Clínico" cor="#ec4899" itens={[
            F('/gestao-monitoramento-clinico', '📊', 'Monitoramento Clínico'),
            F('/gestao-feridas', '🩹', 'Gestão de Feridas'),
            F('/gestao-ouvidorias', '💬', 'Ouvidorias'),
          ]} />
          <Bloco icon="📦" titulo="Solicitações" cor="#f97316" itens={[
            F('/gestao-previa-recargas', '🔋', 'Prévia de Recargas'),
            F('/gestao-remocoes', '🚑', 'Remoção'),
            F('/gestao-solicitacao-troca', '🔄', 'Troca de Dispositivo'),
            F('/gestao-solicitacao-exames', '🔬', 'Exames'),
          ]} />
          <Bloco icon="🏬" titulo="Estoque" cor="#eab308" itens={[
            C(CAPABILITIES.VIEW_ESTOQUE_CONTROLE, '/estoque-controle', '📦', 'Controle'),
            C(CAPABILITIES.VIEW_ESTOQUE_SOLICITACAO, '/estoque-solicitacao', '📝', 'Solicitar'),
            C(CAPABILITIES.VIEW_ESTOQUE_CONSUMO, '/estoque-consumo', '📊', 'Consumo'),
          ]} />
          <Bloco icon="📊" titulo="Dashboards" cor="#0ea5e9" itens={[
            C(CAPABILITIES.VIEW_OCORRENCIAS_SUPERVISOR, '/dashboard-hub', '🗂', 'Hub'),
            C(CAPABILITIES.VIEW_OCORRENCIAS_SUPERVISOR, '/dashboard-implantacao', '📥', 'Captação'),
            C(CAPABILITIES.VIEW_OCORRENCIAS_SUPERVISOR, '/dashboard-hospitalizacao', '🏥', 'Hospitalização'),
            C(CAPABILITIES.VIEW_DASHBOARD, '/dashboard-indicadores', '🏠', 'Censo Homecare'),
            C(CAPABILITIES.VIEW_DASHBOARD, '/dashboard-planilha', '📞', 'Telefonia'),
            C(CAPABILITIES.VIEW_ANALYSIS, '/analysis', '📈', 'Análises'),
            C(CAPABILITIES.VIEW_DASHBOARD, '/dashboard-prontuario', '📄', 'Prontuários'),
            C(CAPABILITIES.VIEW_DASHBOARD, '/dashboard-visitas', '🚗', 'Visitas'),
            F('/gestao-orcamentos', '💰', 'Orçamentos'),
            M('pendencia_orcamentos', '/pendencia-orcamentos', '🧾', 'Pendência de Orçamentos'),
            M('contas_enviadas', '/gestao-contas-enviadas', '📤', 'Envio de Contas'),
            F('/gestao-visitas-produtividade', '🏠', 'Visitas × Produtiv.'),
          ]} />
          <Bloco icon="🚀" titulo="Projetos" cor="#f97316" itens={[
            C(CAPABILITIES.VIEW_PROJETO_IW, '/app', '📋', 'Projeto IW'),
            C(CAPABILITIES.VIEW_PROJETO_IW, '/cronograma-iw', '📅', 'Cronograma IW'),
            C(CAPABILITIES.VIEW_PROJETO_HOMECARE, '/projeto-homecare', '🏗️', 'Projeto Homecare'),
          ]} />
          <Bloco icon="🛡️" titulo="Qualidade" cor="#10b981" itens={[
            C(CAPABILITIES.VIEW_AGENDA_QUALIDADE, '/agenda-qualidade', '📅', 'Agenda'),
            C(CAPABILITIES.VIEW_INDICADORES, '/indicador-agenda', '📈', 'Indicadores'),
          ]} />
          <Bloco icon="⭐" titulo="Direção" cor="#d97706" itens={[
            C(CAPABILITIES.VIEW_AGENDA_DIRECAO, '/agenda-direcao', '📅', 'Agenda Direção'),
            C(CAPABILITIES.VIEW_PROJETO_DIRECAO, '/projeto-direcao', '🚀', 'Projeto Direção'),
            C(CAPABILITIES.VIEW_OBRAS_DIRECAO, '/obras-direcao', '🏗️', 'Obras Direção'),
            modulo('apresentacao_convenio') ? { href: '/apresentacao-convenio/index.html#/home', icon: '🖥️', title: 'Apresentação pro Convênio' } : null,
          ]} />
        </Secao>

        {/* ── Plataforma ─────────────────────────────────────────────────────── */}
        <Secao titulo="Plataforma">
          <Bloco icon="🛠️" titulo="Suporte" cor="#ef4444" itens={[
            C(CAPABILITIES.VIEW_ABRIR_CHAMADO, '/abrir-chamado', '📣', 'Abrir Chamado'),
            C(CAPABILITIES.VIEW_MEUS_CHAMADOS, '/meus-chamados', '📩', 'Meus Chamados'),
            C(CAPABILITIES.VIEW_PAINEL_CHAMADOS, '/painel-chamados', '🛠️', 'Painel Sustentação'),
            C(CAPABILITIES.VIEW_TAREFAS_QUALIDADE, '/tarefas-qualidade', '✅', 'Tarefas da Qualidade'),
          ]} />
          <Bloco icon="🤝" titulo="Portais" cor="#ec4899" itens={[
            C(CAPABILITIES.VIEW_PORTAL_COOPERATIVA, '/portal-cooperativa', '🤝', 'Cooperativa'),
            C(CAPABILITIES.VIEW_AVALIACAO_RH, '/portal-rh', '📋', 'Avaliação RH'),
            C(CAPABILITIES.VIEW_ACESSOS_TI, '/portal-ti', '💻', 'Acessos TI'),
          ]} />
          <Bloco icon="🎓" titulo="INEP — Ensino" cor="#0878b9" itens={[
            M('inep_ensino', '/inep', '🎓', 'INEP — Ensino'),
          ]} />
          <Bloco icon="⚙️" titulo="Administração" cor="#64748b" itens={[
            C(CAPABILITIES.MANAGE_SETTINGS, '/settings', '⚙️', 'Configurações'),
            C(CAPABILITIES.VIEW_GESTAO_PACIENTES, '/uploads', '📤', 'Central de Uploads'),
            C(CAPABILITIES.VIEW_HTML_VIEWER, '/html-viewer', '💻', 'Visualizador HTML'),
            isSuperAdmin ? F('/audit', '🔍', 'Auditoria') : null,
            isSuperAdmin ? F('/admin-eventos', '📆', 'Eventos') : null,
          ]} />
        </Secao>
      </div>
    </div>
  );
}
